// The read-only OEM administration area (documentation/oem-partner-architecture.md section D).
// Presentational components: they show exactly the fields of the OEM-safe /api/oem/* projections
// and nothing operational (no radios, counts, status, GPS, SOS or activity), and offer no actions.
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { OEMCustomerView, OEMDepartmentView, OEMNetworkView, OEMSelf, OEMUserView } from '../../services/oem';
import { OEM_CUSTOMER_TABS, OEM_NAV, departmentNames, formatDate, type OEMCustomerTab } from './oemModel';

export function OEMShell({ oemName, active, badge, children }: { oemName: string; active: string; badge?: ReactNode; children: ReactNode }) {
  return (
    <div className="flex h-screen flex-col overflow-hidden bg-gray-900 text-white">
      <header className="bg-gray-800 shadow">
        <div className="flex flex-wrap items-center gap-6 px-6 py-4">
          <img src="/logo.png" alt="KAN-Consult" className="h-12 w-auto" />
          <div className="min-w-0">
            <h1 className="text-2xl font-bold">RadioOS OEM Administration</h1>
            <p className="text-gray-400 text-sm">{oemName} · read-only administrative view</p>
          </div>
          <nav aria-label="OEM administration" className="flex gap-3">
            {OEM_NAV.map((n) => (
              <Link key={n.path} to={n.path} aria-current={active === n.path ? 'page' : undefined}
                className={`px-4 py-2 rounded font-medium ${active === n.path ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'}`}>
                {n.label}
              </Link>
            ))}
          </nav>
          <div className="ml-auto">{badge}</div>
        </div>
      </header>
      <main className="flex-1 min-h-0 overflow-y-auto">
        <div className="max-w-6xl mx-auto px-6 py-6 space-y-6">{children}</div>
      </main>
    </div>
  );
}

export type PanelState = { kind: 'loading' } | { kind: 'empty'; text: string } | { kind: 'error'; text: string };

export function StatePanel({ state }: { state: PanelState }) {
  if (state.kind === 'loading') return <p role="status" className="text-sm text-gray-400">Loading…</p>;
  if (state.kind === 'empty') return <p className="text-sm text-gray-400">{state.text}</p>;
  return <p role="alert" className="text-sm text-red-200 bg-red-900/40 border border-red-700 rounded p-3">{state.text}</p>;
}

const field = (label: string, value: ReactNode) => (
  <div className="py-1.5 grid grid-cols-3 gap-3 border-t border-gray-700 first:border-t-0">
    <dt className="text-gray-400">{label}</dt>
    <dd className="col-span-2 break-words">{value || <span className="text-gray-500">—</span>}</dd>
  </div>
);

export function OEMOverview({ self }: { self: OEMSelf }) {
  return (
    <section aria-labelledby="oem-overview" className="bg-gray-800 border border-gray-700 rounded p-4">
      <h2 id="oem-overview" className="text-lg font-semibold mb-2">{self.name}</h2>
      <dl className="text-sm">
        {field('Legal name', self.legal_name)}
        {field('Contact', [self.contact_name, self.contact_email, self.contact_phone].filter(Boolean).join(' · '))}
        {field('Support e-mail', self.support_email)}
        {field('Support phone', self.support_phone)}
        {field('Support URL', self.support_url)}
      </dl>
    </section>
  );
}

export function OEMCustomerList({ customers, state }: { customers: OEMCustomerView[]; state?: PanelState }) {
  return (
    <section aria-labelledby="oem-customers" className="bg-gray-800 border border-gray-700 rounded p-4 space-y-3">
      <h2 id="oem-customers" className="text-lg font-semibold">Customers</h2>
      {state ? <StatePanel state={state} /> : customers.length === 0 ? (
        <StatePanel state={{ kind: 'empty', text: 'No customers currently belong to your OEM Partner.' }} />
      ) : (
        <table className="w-full text-sm text-left">
          <thead className="text-gray-400"><tr><th scope="col" className="py-1">Customer</th><th scope="col" className="py-1">Created</th></tr></thead>
          <tbody>
            {customers.map((c) => (
              <tr key={c.id} className="border-t border-gray-700">
                <td className="py-1.5"><Link to={`/OEM/customers/${encodeURIComponent(c.id)}`} className="text-blue-300 hover:underline">{c.name}</Link></td>
                <td className="py-1.5 text-gray-300">{formatDate(c.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

export interface CustomerDetailData {
  networks?: OEMNetworkView[];
  departments?: OEMDepartmentView[];
  users?: OEMUserView[];
}

export function OEMCustomerDetail({ customer, tab, data, state }: { customer: OEMCustomerView; tab: OEMCustomerTab; data: CustomerDetailData; state?: PanelState }) {
  return (
    <section aria-labelledby="oem-customer" className="space-y-4">
      <div>
        <p className="text-sm"><Link to="/OEM" className="text-blue-300 hover:underline">← Customers</Link></p>
        <h2 id="oem-customer" className="text-xl font-bold mt-1">{customer.name}</h2>
        <p className="text-xs text-gray-400">Customer since {formatDate(customer.created_at)} · read-only</p>
      </div>
      <nav aria-label="Customer sections" className="flex gap-2 border-b border-gray-700">
        {OEM_CUSTOMER_TABS.map((t) => (
          <Link key={t.id} to={`/OEM/customers/${encodeURIComponent(customer.id)}/${t.id}`} aria-current={tab === t.id ? 'page' : undefined}
            className={`px-4 py-2 -mb-px border-b-2 text-sm font-medium ${tab === t.id ? 'border-blue-500 text-white' : 'border-transparent text-gray-400 hover:text-gray-200'}`}>
            {t.label}
          </Link>
        ))}
      </nav>
      <div className="bg-gray-800 border border-gray-700 rounded p-4">
        {state ? <StatePanel state={state} /> : tab === 'networks' ? <NetworksTable networks={data.networks || []} />
          : tab === 'departments' ? <DepartmentsTable departments={data.departments || []} />
          : <UsersTable users={data.users || []} networks={data.networks} />}
      </div>
    </section>
  );
}

export function NetworksTable({ networks }: { networks: OEMNetworkView[] }) {
  if (networks.length === 0) return <StatePanel state={{ kind: 'empty', text: 'This customer has no networks.' }} />;
  return (
    <table className="w-full text-sm text-left">
      <caption className="sr-only">Networks</caption>
      <thead className="text-gray-400"><tr><th scope="col" className="py-1">Network</th><th scope="col" className="py-1">Created</th></tr></thead>
      <tbody>
        {networks.map((n) => (
          <tr key={n.id} className="border-t border-gray-700"><td className="py-1.5">{n.name}</td><td className="py-1.5 text-gray-300">{formatDate(n.created_at)}</td></tr>
        ))}
      </tbody>
    </table>
  );
}

export function DepartmentsTable({ departments }: { departments: OEMDepartmentView[] }) {
  if (departments.length === 0) return <StatePanel state={{ kind: 'empty', text: 'This customer has no departments.' }} />;
  const names = departmentNames(departments);
  return (
    <table className="w-full text-sm text-left">
      <caption className="sr-only">Departments</caption>
      <thead className="text-gray-400"><tr><th scope="col" className="py-1">Department</th><th scope="col" className="py-1">Parent</th><th scope="col" className="py-1">Description</th></tr></thead>
      <tbody>
        {departments.map((d) => (
          <tr key={d.id} className="border-t border-gray-700">
            <td className="py-1.5">{d.name}</td>
            <td className="py-1.5 text-gray-300">{d.parent_department_id ? names[d.parent_department_id] || 'Another department' : '—'}</td>
            <td className="py-1.5 text-gray-300">{d.description || '—'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function UsersTable({ users, networks }: { users: OEMUserView[]; networks?: OEMNetworkView[] }) {
  if (users.length === 0) return <StatePanel state={{ kind: 'empty', text: 'This customer has no users.' }} />;
  const networkName = Object.fromEntries((networks || []).map((n) => [n.id, n.name]));
  return (
    <table className="w-full text-sm text-left">
      <caption className="sr-only">Users</caption>
      <thead className="text-gray-400">
        <tr><th scope="col" className="py-1">User</th><th scope="col" className="py-1">E-mail</th><th scope="col" className="py-1">State</th>
          <th scope="col" className="py-1">Home network</th><th scope="col" className="py-1">Roles</th></tr>
      </thead>
      <tbody>
        {users.map((u) => (
          <tr key={u.id} className="border-t border-gray-700 align-top">
            <td className="py-1.5">{u.username}</td>
            <td className="py-1.5 text-gray-300 break-all">{u.email}</td>
            <td className="py-1.5">{u.disabled ? 'Disabled' : 'Active'}</td>
            <td className="py-1.5 text-gray-300">{networkName[u.network_id] || (u.network_id ? 'Network of this customer' : '—')}</td>
            <td className="py-1.5">
              {u.roles.length === 0 ? <span className="text-gray-400">Legacy role: {u.legacy_role}</span> : (
                <ul className="space-y-0.5">
                  {u.roles.map((r, i) => <li key={i}>{r.role}{r.department_name ? ` · Department: ${r.department_name}` : ' · Entire customer'}</li>)}
                </ul>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function OEMNotAvailable() {
  return (
    <section role="alert" className="bg-gray-800 border border-gray-700 rounded p-6 space-y-2">
      <h2 className="text-lg font-semibold">Not available</h2>
      <p className="text-sm text-gray-300">
        This page is not part of OEM administration. Your account has read-only access to your OEM Partner and its customers only.
      </p>
      <p className="text-sm"><Link to="/OEM" className="text-blue-300 hover:underline">Go to OEM overview</Link></p>
    </section>
  );
}
